import { FeatureFlagName } from '@api'
import { sample } from 'effector'
import { noop } from 'lodash/fp'
import { matchPath } from 'react-router'

import { LocalStorageKeys } from '@/consts/localStorage.ts'
import { Pages, isCUI } from '@/consts/routes.ts'
import {
  $fanCategories,
  $isGettingCategoriesError,
  getFanCategoriesFx,
} from '@/dialogs/EditFanOriginDialog/models.ts'
import { DIALOG_TYPE } from '@/dialogs/constants.ts'
import type { DialogEventPayload } from '@/dialogs/declarations.tsx'
import { $fanId } from '@/models/chattingUI/chats/chats.ts'
import {
  addMessageByFanId,
  addMessageToMap,
  getMessagesFx,
} from '@/models/chattingUI/messages/messages.ts'
import { $creatorId, $creatorOFId } from '@/models/creators.ts'
import { openDialog } from '@/models/dialogs.ts'
import { getAvailableFeaturesFx } from '@/models/feature-flags.ts'
import { initializeApp } from '@/models/init.ts'
import { $currentPage, $navigate, $queryParams } from '@/models/router.ts'
import { $vaultMediasMap, updateMediaDataMap } from '@/models/vault/medias'
import {
  startClonedMediaByOriginalsPolling,
  stopClonedMediaByOriginalsPolling,
} from '@/models/vault/medias/clonned'
import { closeVault, openVault } from '@/models/vault/vault.ts'
import type { StreamableVaultMedia } from '@/modules/vault/types.ts'
import { isFeatureAvailable } from '@/utils/featureFlags.ts'

import { $isLoggedInOF } from './chattingUI/OFauth'
import './chattingUI/chats'
import './chattingUI/messages'
import './creator-notes'
import './feature-flags'
import './scripts'
import './theme'
import './vault'

export * from '@/models/ai-features/ai-chatting.ts'

sample({
  source: {
    creatorId: $creatorId,
    fanId: $fanId,
    isLoading: getFanCategoriesFx.pending,
    isError: $isGettingCategoriesError,
    data: $fanCategories,
  },
  filter: ({ creatorId, fanId, data, isError, isLoading }) => {
    return !!creatorId && !!fanId && !isLoading && !data.length && !isError
  },
  target: getFanCategoriesFx,
})

sample({
  clock: openVault,
  target: startClonedMediaByOriginalsPolling,
})

sample({
  clock: closeVault,
  target: stopClonedMediaByOriginalsPolling,
})

sample({
  clock: $navigate,
  filter: (navigate) => navigate !== noop,
  target: [initializeApp],
})

// in chatting ui we would like to load vault data immediately
sample({
  clock: initializeApp,
  source: { path: $currentPage },
  filter: ({ path }) => {
    return !!matchPath(Pages.CHATTING_UI, path)
  },
  target: openVault,
})

/**
 * Open dialog if we got `addCreator` query param
 */
sample({
  clock: $queryParams,
  filter: ({ addCreator }) => !!addCreator,
  fn: () =>
    ({
      type: DIALOG_TYPE.ADD_CREATOR,
    }) as DialogEventPayload,
  target: openDialog,
})

/**
 * Open `Intro` dialog if it was not shown yet.
 */
sample({
  clock: getAvailableFeaturesFx.doneData,
  source: { isLoggedInOF: $isLoggedInOF },
  filter: ({ isLoggedInOF }, availableFeatures) =>
    isLoggedInOF &&
    isCUI &&
    localStorage.getItem(
      LocalStorageKeys.CHATTING_INTRODUCTION_DIALOG_SHOWN,
    ) !== 'true' &&
    sessionStorage.getItem(
      LocalStorageKeys.CHATTING_INTRODUCTION_DIALOG_SHOWN,
    ) !== 'true' &&
    !isFeatureAvailable(availableFeatures, FeatureFlagName.USER_FLOW),
  fn: () =>
    ({
      type: DIALOG_TYPE.CUI_INTRO,
    }) as DialogEventPayload,
  target: openDialog,
})

sample({
  clock: getMessagesFx.doneData,
  source: { creatorOFId: $creatorOFId, mediaMap: $vaultMediasMap },
  fn: ({ creatorOFId, mediaMap }, { list }) => {
    return list.flatMap(({ media, id, fromUser, releaseForms }) => {
      const isMessageFromFan = fromUser.id !== creatorOFId

      return (
        media
          // ignore media that already exists
          ?.filter(({ id }) => !mediaMap[id!])
          .map(
            // @ts-ignore
            (media): StreamableVaultMedia => ({
              ...media,
              isExpirable: isMessageFromFan,
              relatedMessageId: isMessageFromFan ? id : undefined,
              relatedUserId: isMessageFromFan ? fromUser.id : undefined,
              // @ts-ignore
              releaseForms,
              createdAt: '',
              listStates: [],
            }),
          ) ?? []
      )
    })
  },
  target: updateMediaDataMap,
})

sample({
  clock: [addMessageByFanId, addMessageToMap],
  source: { creatorOFId: $creatorOFId, mediaMap: $vaultMediasMap },
  fn: (
    { creatorOFId, mediaMap },
    { message: { media, id, fromUser, releaseForms } },
  ) => {
    const isMessageFromFan = fromUser.id !== creatorOFId

    return (
      media
        ?.filter(({ id }) => !mediaMap[id!])
        .map(
          // @ts-ignore
          (media): StreamableVaultMedia => ({
            ...media,
            isExpirable: isMessageFromFan,
            relatedMessageId: isMessageFromFan ? id : undefined,
            relatedUserId: isMessageFromFan ? fromUser.id : undefined,
            // @ts-ignore
            releaseForms,
            createdAt: '',
            listStates: [],
          }),
        ) ?? []
    )
  },
  target: updateMediaDataMap,
})
